import React from "react";
import { Warning, CircleNotch, ArrowSquareOut, CaretLeft, CaretRight, PushPin, Stack, SealCheck } from "@phosphor-icons/react";
import { cn, decodeAssetName, decodeAssetNameSmart, truncMid, unixToDate, formatRelativeTime } from "@/lib/format";
import { CopyBtn } from "@/components/primitives";
import { useWatchlist } from "@/components/assets/watchlist";
import { useTokenMeta } from "@/components/assets/tokenMeta";

export const WINDOWS = ["24h", "7d", "30d"];

export const fmtInt = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("en-US"));

// CIP-67/68 asset names carry a 4-byte label prefix (e.g. 0014df10 = FT-333,
// 000de140 = NFT-222, 000643b0 = ref-100) before the readable bytes. The strict
// UTF-8 decoder rejects the leading 0x00, so strip the prefix and retry — this
// recovers names like "SLIP"/"MEPE"/"VAL" that would otherwise show as an id.
// (Also unwraps double hex-encoded names.) Shared impl lives in lib/format.
export { decodeAssetNameSmart };

// CIP-67/68 asset-class label (from the 4-byte name prefix): 100=reference,
// 222=NFT, 333=FT, 444=rich-FT. Lets same-named tokens under one policy (e.g. a
// 222 NFT + its 100 reference) be told apart.
const CIP68_KINDS = { 100: "REF", 222: "NFT", 333: "FT", 444: "RFT" };
export const cip68Label = (assetNameHex) => {
  if (!assetNameHex || assetNameHex.length < 8 || !/^00[0-9a-f]{6}/i.test(assetNameHex)) return null;
  const num = parseInt(assetNameHex.slice(2, 6), 16) >> 4;
  const kind = CIP68_KINDS[num];
  return kind ? { num, kind } : null;
};

export const Cip68Badge = ({ assetName, testid }) => {
  const lbl = cip68Label(assetName);
  if (!lbl) return null;
  return (
    <span
      data-testid={testid}
      title={`CIP-68 label ${lbl.num} — ${lbl.kind === "REF" ? "reference token" : lbl.kind === "NFT" ? "non-fungible" : lbl.kind === "RFT" ? "rich fungible" : "fungible"}`}
      className="shrink-0 text-[9px] uppercase tracking-[0.08em] mono px-1 py-0.5 rounded bg-primary/15 text-primary/90 border border-primary/25"
    >
      {lbl.kind}
    </span>
  );
};

// Mint/burn quantities arrive as strings and can exceed 2^53 — format with
// BigInt so precision is never lost.
export const fmtQty = (s) => {
  if (s === null || s === undefined || s === "") return "—";
  try {
    return BigInt(s).toLocaleString("en-US");
  } catch {
    return String(s);
  }
};

// Decimal-aware quantity using registry metadata: shows human units + ticker
// for registered tokens (e.g. 1,250 MIN), raw grouped value otherwise.
export const fmtQtyMeta = (raw, meta) => {
  if (raw === null || raw === undefined || raw === "") return "—";
  const ticker = meta?.ticker ? ` ${meta.ticker}` : "";
  const dec = Number(meta?.decimals) || 0;
  const s = String(raw);
  if (!/^\d+$/.test(s)) return fmtQty(raw) + ticker;
  try {
    if (dec > 0) {
      const d = 10n ** BigInt(dec);
      const whole = (BigInt(s) / d).toLocaleString("en-US");
      const frac = (BigInt(s) % d).toString().padStart(dec, "0").slice(0, 2).replace(/0+$/, "");
      return whole + (frac ? `.${frac}` : "") + ticker;
    }
    return BigInt(s).toLocaleString("en-US") + ticker;
  } catch {
    return String(raw) + ticker;
  }
};

export const VerifiedTick = ({ testid }) => (
  <SealCheck
    data-testid={testid}
    size={13}
    weight="fill"
    className="shrink-0 text-[#22d3ee]"
  >
    <title>Verified by the Cardano token registry</title>
  </SealCheck>
);

// Readable label for an asset: decoded UTF-8 name, else fingerprint, else a
// shortened hex ident. Never renders undecodable bytes.
export const assetLabel = (item) => {
  const name = decodeAssetNameSmart(item?.asset_name || "");
  if (name) return { text: name, decoded: true };
  if (item?.fingerprint) return { text: `${item.fingerprint.slice(0, 16)}…`, decoded: false };
  if (item?.asset_name) return { text: `#${item.asset_name.slice(0, 16)}`, decoded: false };
  return { text: truncMid(item?.asset || "", 10, 6), decoded: false };
};

// Registry logo (official) with a deterministic monogram fallback.
export const TokenLogo = ({ item, size = 28, testid }) => {
  const { meta } = useTokenMeta();
  const m = item?.asset ? meta.get(item.asset) : null;
  if (m && m.logo) {
    return (
      <img
        data-testid={testid}
        src={m.logo}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        className="rounded-md object-cover shrink-0 ring-1 ring-white/10 bg-black/30"
        style={{ width: size, height: size }}
      />
    );
  }
  return <Avatar item={item} size={size} testid={testid} />;
};

// Pin toggle — adds/removes an asset from the localStorage watchlist.
export const WatchPin = ({ item, testid }) => {
  const { list, toggle } = useWatchlist();
  const pinned = list.some((x) => x.asset === item.asset);
  return (
    <button
      type="button"
      data-testid={testid}
      data-pinned={pinned ? "1" : "0"}
      onClick={(e) => { e.stopPropagation(); toggle(item); }}
      title={pinned ? "Remove from watchlist" : "Add to watchlist"}
      aria-label={pinned ? "Remove from watchlist" : "Add to watchlist"}
      className={cn("shrink-0 inline-flex items-center justify-center h-5 w-5 rounded transition-colors", pinned ? "text-[#22d3ee]" : "text-muted-foreground/50 hover:text-[#22d3ee]")}
    >
      <PushPin size={12} weight={pinned ? "fill" : "regular"} />
    </button>
  );
};

// Clickable asset name → existing asset detail page, plus copy, optional policy
// drill-down, and a watchlist pin.
export const AssetName = ({ item, onPick, onOpenPolicy, pin = true, testid }) => {
  const { meta } = useTokenMeta();
  const reg = item?.asset ? meta.get(item.asset) : null;
  const { text } = assetLabel(item);
  const display = reg?.name || reg?.ticker || text;
  return (
    <span className="inline-flex items-center gap-1 min-w-0 max-w-full">
      <button
        type="button"
        data-testid={testid}
        onClick={() => onPick?.(item.asset)}
        title="Open asset details"
        className="truncate text-left mono text-[13px] text-[#22d3ee] hover:underline underline-offset-2 inline-flex items-center gap-1 min-w-0 flex-1"
      >
        <span className="truncate">{display}</span>
        <ArrowSquareOut size={11} className="shrink-0 opacity-70" />
      </button>
      {reg?.name ? <VerifiedTick testid={testid ? `${testid}-verified` : undefined} /> : null}
      <Cip68Badge assetName={item?.asset_name} testid={testid ? `${testid}-cip68` : undefined} />
      <CopyBtn value={item.asset} label="asset" />
      {onOpenPolicy && item.policy_id ? (
        <button
          type="button"
          data-testid={testid ? `${testid}-policy` : undefined}
          onClick={(e) => { e.stopPropagation(); onOpenPolicy(item.policy_id); }}
          title="View all assets under this policy"
          aria-label="View policy assets"
          className="shrink-0 inline-flex items-center justify-center h-5 w-5 rounded text-muted-foreground/60 hover:text-primary transition-colors"
        >
          <Stack size={12} />
        </button>
      ) : null}
      {pin ? <WatchPin item={item} testid={testid ? `${testid}-pin` : undefined} /> : null}
    </span>
  );
};

// Percentage change: positive green, negative red, null => "New".
export const Delta = ({ pct, testid }) => {
  if (pct === null || pct === undefined) {
    return <span data-testid={testid} className="text-[11px] mono px-1.5 py-0.5 rounded-md text-[#22d3ee] bg-[#22d3ee]/10">NEW</span>;
  }
  const up = pct >= 0;
  return (
    <span data-testid={testid} className={cn("text-[12px] mono tabnum px-1.5 py-0.5 rounded-md", up ? "text-emerald-400 bg-emerald-400/10" : "text-rose-400 bg-rose-400/10")}>
      {up ? "+" : ""}{Number(pct).toFixed(2)}%
    </span>
  );
};

// Section card matching the Explorer's widget chrome.
export const Panel = ({ title, icon: Icon, right, children, testid, className }) => (
  <div data-testid={testid} className={cn("relative overflow-hidden border border-primary/25 bg-card/30 backdrop-blur-md rounded-2xl box-glow", className)}>
    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-60 tracing-beam pointer-events-none" />
    <div className="px-5 py-3 border-b border-primary/15 flex items-center justify-between gap-3 flex-wrap">
      <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#22d3ee] mono">
        {Icon ? <Icon size={14} weight="duotone" /> : null}
        {title}
      </span>
      {right}
    </div>
    {children}
  </div>
);

export const WindowTabs = ({ value, onChange, options = WINDOWS, testid }) => (
  <div className="inline-flex border border-primary/15 rounded-lg overflow-hidden" data-testid={testid}>
    {options.map((o) => (
      <button
        key={o}
        type="button"
        data-testid={`${testid}-${o}`}
        onClick={() => onChange(o)}
        className={cn(
          "px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] mono transition-colors",
          String(value) === String(o) ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground",
        )}
      >
        {o}
      </button>
    ))}
  </div>
);

// Freshness + completeness indicator derived from any analytics response.
export const Freshness = ({ freshness, windowComplete, testid }) => {
  if (!freshness) return null;
  const stale = freshness.stale === true;
  const asOf = freshness.block_time ? unixToDate(freshness.block_time) : null;
  const partial = windowComplete === false;
  return (
    <span
      data-testid={testid}
      data-stale={stale ? "1" : "0"}
      className={cn(
        "inline-flex items-center gap-1.5 text-[10px] mono px-2 py-0.5 rounded-md whitespace-nowrap",
        stale ? "text-amber-300 bg-amber-400/10" : "text-muted-foreground/80",
      )}
      title={asOf ? `Data as of ${asOf} (block ${freshness.block_no})` : ""}
    >
      {stale ? <Warning size={11} /> : null}
      {stale ? "STALE · " : ""}as of block {fmtInt(freshness.block_no)}
      {freshness.block_time ? ` · ${formatRelativeTime(freshness.block_time)}` : ""}
      {partial ? " · PARTIAL" : ""}
    </span>
  );
};

export const ErrorState = ({ error, onRetry, testid }) => (
  <div data-testid={testid} className="px-5 py-8 flex flex-col items-center gap-3 text-center">
    <Warning size={22} className={error?.kind === "unavailable" ? "text-amber-400" : "text-rose-400"} />
    <div className="text-[13px] text-muted-foreground mono max-w-sm">{error?.message || "Something went wrong."}</div>
    {onRetry ? (
      <button
        type="button"
        data-testid={testid ? `${testid}-retry` : undefined}
        onClick={onRetry}
        className="px-3 py-1.5 border border-primary/40 bg-primary/10 text-primary text-[10px] uppercase tracking-[0.18em] mono rounded hover:bg-primary/20"
      >
        Retry
      </button>
    ) : null}
  </div>
);

export const EmptyState = ({ label, testid }) => (
  <div data-testid={testid} className="px-5 py-10 text-center text-[12px] text-muted-foreground mono">{label || "Nothing to show yet."}</div>
);

export const Spinner = () => <CircleNotch size={16} className="animate-spin text-primary" />;

export const SkeletonRows = ({ rows = 6, cols = 4 }) => (
  <tbody>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={r} className="border-b border-primary/5">
        {Array.from({ length: cols }).map((__, c) => (
          <td key={c} className="px-4 py-2.5">
            <span className="inline-block h-3 w-20 bg-primary/10 animate-pulse rounded-sm" />
          </td>
        ))}
      </tr>
    ))}
  </tbody>
);

// ---- visual helpers for the redesigned dashboard ----

const AVATAR_PALETTE = [
  ["#22d3ee", "#0ea5e9"],
  ["#a855f7", "#6366f1"],
  ["#f59e0b", "#ef4444"],
  ["#10b981", "#14b8a6"],
  ["#ec4899", "#8b5cf6"],
  ["#eab308", "#f97316"],
  ["#38bdf8", "#818cf8"],
];
export const assetColor = (seed = "") => {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};

// Deterministic monogram avatar — the upstream API carries no token logos, so
// we derive a stable colour + initial from the policy id. Never a fake logo.
export const Avatar = ({ item, size = 28, testid }) => {
  const { text } = assetLabel(item);
  const letter = ((text || "").replace(/[^a-zA-Z0-9]/g, "")[0] || "#").toUpperCase();
  const [a, b] = assetColor(item?.policy_id || item?.asset || text || "");
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center justify-center rounded-md font-bold shrink-0 font-sans ring-1 ring-white/10"
      style={{ width: size, height: size, background: `linear-gradient(135deg, ${a}, ${b})`, fontSize: size * 0.42, color: "#07111f" }}
    >
      {letter}
    </span>
  );
};

// Tiny bar sparkline (aggregate series — never per-asset fabricated data).
export const MiniBars = ({ values = [], color = "#22d3ee", width = 72, height = 28 }) => {
  const max = Math.max(1, ...values.map((v) => Number(v) || 0));
  const bw = Math.max(2, Math.floor(width / Math.max(1, values.length)) - 2);
  return (
    <span className="inline-flex items-end gap-[2px]" style={{ height }} aria-hidden="true">
      {values.map((v, i) => (
        <span
          key={i}
          className="rounded-[1px]"
          style={{ height: `${Math.max(10, ((Number(v) || 0) / max) * 100)}%`, width: bw, background: color, opacity: 0.4 + 0.6 * (i / Math.max(1, values.length - 1)) }}
        />
      ))}
    </span>
  );
};

// Tiny line sparkline from a real value series.
export const MiniSpark = ({ values = [], color = "#22d3ee", width = 72, height = 26 }) => {
  const nums = values.map((v) => Number(v) || 0);
  if (nums.length < 2) return <span style={{ width, height }} className="inline-block" aria-hidden="true" />;
  const max = Math.max(...nums);
  const min = Math.min(...nums);
  const rng = max - min || 1;
  const pts = nums
    .map((v, i) => `${((i / (nums.length - 1)) * width).toFixed(1)},${(height - ((v - min) / rng) * (height - 4) - 2).toFixed(1)}`)
    .join(" ");
  return (
    <svg width={width} height={height} className="overflow-visible" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// Drops the final chart bucket when it is the still-in-progress current period
// (its block count is well below the recent average), so period-over-period
// deltas and the trend line don't show a false cliff from a half-finished day.
export const trimPartial = (points) => {
  if (!points || points.length < 3) return points || [];
  const last = points[points.length - 1];
  const prev = points[points.length - 2];
  const span = Math.max(1, (Number(last.bucket_start) || 0) - (Number(prev.bucket_start) || 0));
  const nowSec = Date.now() / 1000;
  // In-progress period: its window hasn't fully elapsed yet (robust all day).
  if ((Number(last.bucket_start) || 0) + span > nowSec) return points.slice(0, -1);
  // Fallback: clearly fewer blocks than the recent average.
  const prior = points.slice(-8, -1);
  const avgBlocks = prior.reduce((s, p) => s + (Number(p.blocks) || 0), 0) / (prior.length || 1);
  if (avgBlocks > 0 && (Number(last.blocks) || 0) < avgBlocks * 0.6) return points.slice(0, -1);
  return points;
};

// Period-over-period delta for an ADDITIVE field (tx/new/mint/burn counts).
// Returns null when fewer than 2 full windows of data exist.
export const windowDelta = (points, field, n) => {
  if (!points || !n || points.length < n * 2) return null;
  const sum = (arr) => arr.reduce((s, p) => s + (Number(p[field]) || 0), 0);
  const cur = sum(points.slice(-n));
  const prev = sum(points.slice(-n * 2, -n));
  const pct = prev === 0 ? null : ((cur - prev) / prev) * 100;
  return { cur, prev, delta: cur - prev, pct };
};

// Average-per-bucket delta — for DISTINCT-count fields (active_assets) that are
// not additive across buckets.
export const avgDelta = (points, field, n) => {
  if (!points || !n || points.length < n * 2) return null;
  const avg = (arr) => (arr.length ? arr.reduce((s, p) => s + (Number(p[field]) || 0), 0) / arr.length : 0);
  const cur = avg(points.slice(-n));
  const prev = avg(points.slice(-n * 2, -n));
  const pct = prev === 0 ? null : ((cur - prev) / prev) * 100;
  return { cur, prev, pct };
};

// Signed "vs previous" footer line used on the metric cards.
export const VsPrevious = ({ pct, delta, label, testid }) => {
  if (pct === null || pct === undefined) {
    return <span data-testid={testid} className="text-[10px] mono text-muted-foreground/70">{label || "no prior period"}</span>;
  }
  const up = pct >= 0;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1.5 text-[11px] mono">
      <span className="text-muted-foreground/70">{label}</span>
      <span className={cn("tabnum", up ? "text-emerald-400" : "text-rose-400")}>{up ? "+" : ""}{Number(pct).toFixed(2)}%</span>
      {delta !== undefined && delta !== null ? (
        <span className={cn("tabnum", up ? "text-emerald-400/80" : "text-rose-400/80")}>{up ? "+" : ""}{fmtInt(delta)}</span>
      ) : null}
    </span>
  );
};

export const Pager = ({ page, setPage, hasNext, loading, testid }) => (
  <div className="flex items-center justify-end gap-2 px-5 py-2.5 border-t border-primary/10">
    <button
      type="button"
      data-testid={`${testid}-prev`}
      disabled={page <= 1 || loading}
      onClick={() => setPage(Math.max(1, page - 1))}
      className="inline-flex items-center gap-1 px-2.5 py-1 border border-primary/20 rounded text-[10px] uppercase tracking-[0.15em] mono text-muted-foreground hover:text-foreground disabled:opacity-40"
    >
      <CaretLeft size={11} /> Prev
    </button>
    <span data-testid={`${testid}-page`} className="text-[11px] mono text-muted-foreground tabnum px-1">{page}</span>
    <button
      type="button"
      data-testid={`${testid}-next`}
      disabled={!hasNext || loading}
      onClick={() => setPage(page + 1)}
      className="inline-flex items-center gap-1 px-2.5 py-1 border border-primary/20 rounded text-[10px] uppercase tracking-[0.15em] mono text-muted-foreground hover:text-foreground disabled:opacity-40"
    >
      Next <CaretRight size={11} />
    </button>
  </div>
);
